import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tableStyles } from '../ui';
import {
  getPermissionRegistry,
  listPermissionAssignments,
  grantPermission,
  revokePermission,
  offboardUser,
  PermissionDescriptor,
  PermissionUserRow,
  PendingPermissionRow,
} from '../../services/permissionsAdminApi';
import type { PermissionKey } from '../../hooks/usePermissions';
import { DASHBOARDS } from '../../config/dashboards';
import { orderPermissionColumns } from './permissionColumns';

const PermissionsTab: React.FC = () => {
  const [registry, setRegistry] = useState<PermissionDescriptor[]>([]);
  const [users, setUsers] = useState<PermissionUserRow[]>([]);
  const [pending, setPending] = useState<PendingPermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [offboardTarget, setOffboardTarget] = useState<PermissionUserRow | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reg, assignments] = await Promise.all([
        getPermissionRegistry(),
        listPermissionAssignments(),
      ]);
      setRegistry(reg);
      setUsers(assignments.users);
      setPending(assignments.pending);
    } catch (err: any) {
      setError(err.message || 'Failed to load permissions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3000);
    return () => clearTimeout(t);
  }, [flash]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.displayName ?? '').toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q),
    );
  }, [users, search]);

  // Columns follow the dashboard picker's order, not the backend registry's.
  const columns = useMemo(() => orderPermissionColumns(registry, DASHBOARDS), [registry]);

  const togglePermission = async (
    target: { email: string; uid?: string },
    key: PermissionKey,
    next: boolean,
  ) => {
    const busyKey = `${target.email}::${key}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      if (next) {
        const result = await grantPermission(target.email, key);
        setFlash(
          result.status === 'pending'
            ? `Pending: activates on next sign-in for ${target.email}.`
            : `Granted ${key} to ${target.email}.`,
        );
      } else {
        await revokePermission(target.email, key);
        setFlash(`Revoked ${key} from ${target.email}.`);
      }
      await reload();
    } catch (err: any) {
      setError(err.message || 'Failed to update permission.');
    } finally {
      setBusy((b) => {
        const copy = { ...b };
        delete copy[busyKey];
        return copy;
      });
    }
  };

  if (loading) {
    return <p style={styles.muted}>Loading permissions…</p>;
  }

  return (
    <div style={styles.container}>
      {flash && <div style={styles.flash}>{flash}</div>}
      {error && <div style={styles.errorBar}>{error}</div>}

      <div style={styles.toolbar}>
        <input
          type="text"
          value={search}
          placeholder="Search by name or email"
          onChange={(e) => setSearch(e.target.value)}
          style={styles.search}
        />
        <button onClick={() => setShowAddModal(true)} style={styles.primaryBtn}>
          + Add user by email
        </button>
        <button onClick={reload} style={styles.secondaryBtn}>
          Refresh
        </button>
      </div>

      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Active users</h3>
        <div style={styles.matrixWrap}>
          <table style={styles.matrixTable}>
            <thead>
              <tr>
                <th style={styles.thUser}>User</th>
                {columns.map((r) => (
                  <th key={r.key} style={styles.thSticky} title={r.description}>
                    {r.label}
                  </th>
                ))}
                <th style={styles.thSticky}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 && (
                <tr>
                  <td style={styles.td} colSpan={2 + columns.length}>
                    <span style={styles.muted}>No users match.</span>
                  </td>
                </tr>
              )}
              {filteredUsers.map((u) => (
                <tr key={u.uid}>
                  <td style={styles.tdUser}>
                    <div style={styles.userCell}>
                      {u.photoURL ? (
                        <img src={u.photoURL} alt="" style={styles.avatar} />
                      ) : (
                        <div style={styles.avatarFallback}>
                          {(u.displayName ?? u.email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={styles.userInfo} title={u.email}>
                        <span style={styles.userName}>
                          {u.displayName || u.email}
                        </span>
                        <span style={styles.userEmail}>{u.email}</span>
                      </div>
                    </div>
                  </td>
                  {columns.map((r) => {
                    const checked = u.permissions[r.key] === true;
                    const busyKey = `${u.email}::${r.key}`;
                    return (
                      <td key={r.key} style={styles.td}>
                        <label style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!!busy[busyKey]}
                            onChange={(e) =>
                              togglePermission({ email: u.email, uid: u.uid }, r.key, e.target.checked)
                            }
                            style={styles.checkbox}
                          />
                          <span style={styles.checkboxText}>
                            {checked ? 'Granted' : 'No access'}
                          </span>
                        </label>
                      </td>
                    );
                  })}
                  <td style={styles.td}>
                    <button
                      onClick={() => setOffboardTarget(u)}
                      style={styles.smallDangerBtn}
                      title="Disable this account and revoke all access"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {pending.length > 0 && (
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Pending grants</h3>
          <p style={styles.muted}>
            Activates on the user's next sign-in.
          </p>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Permissions</th>
                  <th style={styles.th}>Staged at</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => {
                  const grantedKeys = (Object.keys(p.permissions) as PermissionKey[])
                    .filter((k) => p.permissions[k] === true);
                  return (
                    <tr key={p.email}>
                      <td style={styles.td}>{p.email}</td>
                      <td style={styles.td}>
                        {grantedKeys.map((k) => (
                          <span key={k} style={styles.pill}>
                            {registry.find((r) => r.key === k)?.label ?? k}
                          </span>
                        ))}
                      </td>
                      <td style={styles.td}>
                        {new Date(p.createdAt).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        {grantedKeys.map((k) => (
                          <button
                            key={k}
                            onClick={() => togglePermission({ email: p.email }, k, false)}
                            style={styles.smallDangerBtn}
                            disabled={!!busy[`${p.email}::${k}`]}
                          >
                            Revoke {registry.find((r) => r.key === k)?.label ?? k}
                          </button>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {showAddModal && (
        <AddByEmailModal
          registry={columns}
          onClose={() => setShowAddModal(false)}
          onGranted={async (msg) => {
            setShowAddModal(false);
            setFlash(msg);
            await reload();
          }}
        />
      )}

      {offboardTarget && (
        <OffboardConfirmModal
          user={offboardTarget}
          onClose={() => setOffboardTarget(null)}
          onDone={async (msg) => {
            setOffboardTarget(null);
            setFlash(msg);
            await reload();
          }}
          onError={(msg) => {
            setOffboardTarget(null);
            setError(msg);
          }}
        />
      )}
    </div>
  );
};

interface OffboardModalProps {
  user: PermissionUserRow;
  onClose: () => void;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}

const OffboardConfirmModal: React.FC<OffboardModalProps> = ({ user, onClose, onDone, onError }) => {
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await offboardUser(user.email);
      await onDone(`Removed ${user.displayName || user.email} — account disabled and access revoked.`);
    } catch (e: any) {
      onError(e.message || 'Failed to remove user.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.sectionTitle}>Remove {user.displayName || user.email}?</h3>
        <p style={styles.muted}>
          This disables their Firebase account, signs them out everywhere (revokes
          sessions), clears their stored Google/Slack tokens, and removes all dashboard
          permissions. They will lose access to the app entirely.
        </p>
        <p style={styles.muted}>
          Reversible: re-enable the account from the Firebase console and re-grant
          permissions. The user reconnects Google/Slack via the normal sign-in flow.
        </p>
        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryBtn} disabled={submitting}>
            Cancel
          </button>
          <button onClick={confirm} style={styles.dangerBtn} disabled={submitting}>
            {submitting ? 'Removing…' : 'Remove user'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface AddModalProps {
  registry: PermissionDescriptor[];
  onClose: () => void;
  onGranted: (message: string) => Promise<void>;
}

const AddByEmailModal: React.FC<AddModalProps> = ({ registry, onClose, onGranted }) => {
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<Partial<Record<PermissionKey, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setErr('Email is required.');
      return;
    }
    const keys = (Object.keys(selected) as PermissionKey[]).filter((k) => selected[k]);
    if (keys.length === 0) {
      setErr('Pick at least one permission.');
      return;
    }
    setSubmitting(true);
    try {
      const results = await Promise.all(keys.map((k) => grantPermission(trimmed, k)));
      const pendingCount = results.filter((r) => r.status === 'pending').length;
      const message = pendingCount
        ? `Staged ${keys.length} permission(s) — activates on next sign-in.`
        : `Granted ${keys.length} permission(s) to ${trimmed}.`;
      await onGranted(message);
    } catch (e: any) {
      setErr(e.message || 'Failed to grant permission.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.sectionTitle}>Add user by email</h3>
        <p style={styles.muted}>
          If the user has signed in before, the permission applies immediately.
          Otherwise it's staged and activates on their next sign-in.
        </p>
        <input
          type="email"
          placeholder="user@culturegenesis.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.search}
          autoFocus
        />
        <div style={styles.checkList}>
          {registry.map((r) => (
            <label key={r.key} style={styles.modalCheckRow}>
              <input
                type="checkbox"
                checked={!!selected[r.key]}
                onChange={(e) =>
                  setSelected((prev) => ({ ...prev, [r.key]: e.target.checked }))
                }
              />
              <span>
                <strong>{r.label}</strong>
                <div style={styles.muted}>{r.description}</div>
              </span>
            </label>
          ))}
        </div>
        {err && <div style={styles.errorBar}>{err}</div>}
        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryBtn} disabled={submitting}>
            Cancel
          </button>
          <button onClick={submit} style={styles.primaryBtn} disabled={submitting}>
            {submitting ? 'Granting…' : 'Grant'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '24px' },
  toolbar: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
  search: {
    flex: '1 1 280px',
    minWidth: '240px',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-sans)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
  },
  primaryBtn: {
    padding: '10px 16px',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '44px',
  },
  secondaryBtn: {
    padding: '10px 16px',
    background: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: '44px',
  },
  smallDangerBtn: {
    padding: '6px 10px',
    marginRight: '8px',
    background: 'transparent',
    color: 'var(--danger)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    fontSize: '12px',
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '10px 16px',
    background: 'var(--danger)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '44px',
  },
  section: { display: 'flex', flexDirection: 'column', gap: '10px' },
  sectionTitle: { margin: 0, fontSize: '16px', fontWeight: 600 },
  tableWrap: tableStyles.wrapScroll,
  table: tableStyles.table,
  th: tableStyles.th,
  td: {
    ...tableStyles.td,
    verticalAlign: 'middle',
  },
  // Permission matrix: its own scrollport (both axes), so the header row can
  // stick to its top and the User column to its left — sticky can't reach the
  // page through an overflow-x wrapper. Same machinery as .inv-table-wrap.
  matrixWrap: {
    ...tableStyles.wrap,
    overflow: 'auto',
    // ≈ app header + frame header/tabs + toolbar above the box (measured 353px).
    maxHeight: 'max(320px, calc(100vh - 390px))',
  },
  // separate, not collapse: collapsed borders detach from sticky cells.
  matrixTable: { ...tableStyles.table, borderCollapse: 'separate', borderSpacing: 0 },
  thSticky: tableStyles.thSticky,
  // Corner cell sits above both the sticky header row and the pinned column.
  thUser: {
    ...tableStyles.thSticky,
    left: 0,
    zIndex: 3,
    borderRight: '1px solid var(--border-1)',
  },
  // Opaque white: --bg-surface-1 is 96% alpha and scrolled cells would bleed through.
  tdUser: {
    ...tableStyles.td,
    position: 'sticky',
    left: 0,
    zIndex: 1,
    minWidth: 'min(220px, 35vw)',
    background: 'var(--cg-neutral-0)',
    borderRight: '1px solid var(--border-1)',
  },
  // vw cap keeps the pinned column from eating a phone-width table.
  userCell: { display: 'flex', alignItems: 'center', gap: '10px', maxWidth: 'min(240px, 35vw)' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' as const, flexShrink: 0 },
  avatarFallback: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'var(--accent)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '14px',
  },
  userInfo: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  userName: { fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userEmail: { color: 'var(--fg-3)', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  checkboxLabel: { display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minHeight: '44px' },
  checkbox: { width: '18px', height: '18px', cursor: 'pointer' },
  checkboxText: { fontSize: '13px', color: 'var(--fg-2)' },
  pill: {
    display: 'inline-block',
    padding: '2px 8px',
    marginRight: '4px',
    background: 'var(--accent-soft)',
    borderRadius: 'var(--radius-pill)',
    fontSize: '12px',
    color: 'var(--fg-1)',
  },
  muted: { color: 'var(--fg-3)', fontSize: '13px', margin: 0 },
  flash: {
    padding: '10px 14px',
    background: 'var(--success-soft)',
    color: 'var(--success)',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
  },
  errorBar: {
    padding: '10px 14px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    background: 'var(--bg-surface-1)',
    borderRadius: 'var(--radius-md)',
    padding: '24px',
    maxWidth: '520px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: 'var(--shadow-lg)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  checkList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  modalCheckRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '10px',
    alignItems: 'start',
    padding: '10px',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
};

export default PermissionsTab;
